import { queryOptions, useQuery } from '@tanstack/react-query';

import { is403Error, overrideOrganizationHeader } from '@cast/api-client';
import { RbacServiceAPIListRolesTypeEnum } from '@cast/types-organization-management';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const ENTERPRISE_ROLE_DATA_QUERY_KEY = 'enterprise-role-data';

const getEnterpriseRoleDataQueryKey = (
  params: { enterpriseId?: string; subjectId?: string } = {}
) => {
  if (!params.enterpriseId) {
    return [ENTERPRISE_ROLE_DATA_QUERY_KEY] as const;
  }

  if (params.subjectId) {
    return [
      ENTERPRISE_ROLE_DATA_QUERY_KEY,
      params.enterpriseId,
      params.subjectId,
    ] as const;
  }

  return [ENTERPRISE_ROLE_DATA_QUERY_KEY, params.enterpriseId] as const;
};

const getEnterpriseRoleDataQueryOptions = ({
  enterpriseId,
  subjectId,
  enabled = true,
}: {
  enterpriseId: string;
  subjectId: string;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getEnterpriseRoleDataQueryKey({ enterpriseId, subjectId }),
    queryFn: async () => {
      try {
        const [bindingsResponse, rolesResponse] = await Promise.all([
          apiClient.enterprises.listRoleBindings(
            {
              enterpriseId,
              subjectId: [subjectId],
              pageLimit: '100',
            },
            {
              headers: overrideOrganizationHeader(enterpriseId),
            }
          ),
          apiClient.rbac.listRoles(
            {
              organizationId: enterpriseId,
              pageLimit: '9999',
              type: RbacServiceAPIListRolesTypeEnum.ENTERPRISE,
            },
            {
              headers: overrideOrganizationHeader(enterpriseId),
            }
          ),
        ]);

        return {
          roleBindings: bindingsResponse.data.items ?? [],
          roles: rolesResponse.data.roles ?? [],
        };
      } catch (error) {
        if (is403Error(error)) {
          return { roleBindings: [], roles: [] };
        }
        throw error;
      }
    },
    enabled: makeIsEnabled(!!enterpriseId, !!subjectId, enabled),
  });

export const useEnterpriseRoleDataQuery = ({
  enterpriseId,
  subjectId,
  enabled = true,
}: {
  enterpriseId?: string;
  subjectId: string;
  enabled?: boolean;
}) =>
  useQuery(
    getEnterpriseRoleDataQueryOptions({
      enterpriseId: enterpriseId ?? '',
      subjectId,
      enabled,
    })
  );
